import React from 'react';
import Head from 'next/head';
import { useAlertContext } from '../components/hooks/AlertContext';

import { EnvelopeIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useRouter } from 'next/router';

import { ToastContainer, toast } from 'react-toastify';
import Loading from '../components/common/Loading';
import Link from 'next/link';

import useAuth from '../components/hooks/useNewAuth';

import { sendMessage } from '../notSharedWithOtherPlatform/sendMessage';
import createMagic from '../notSharedWithOtherPlatform/magic';
import { CHECK_ORG_EXISTS_MESSAGE, LOGIN_MESSAGE } from '../lib/MessageTypes';
import toaster from '../lib/toaster';
import createOktaClient, {
  oktaOrgs,
  OKTA_CALLBACK_URL,
  SSO_ORG_DOMAINS,
} from '../lib/okta';
import SsoLoginSelector from '../components/SsoLoginSelector';

const SignInPage = () => {
  const router = useRouter();
  const [showLoading, setShowLoading] = React.useState(false);
  const [loadingMessage, setLoadingMessage] = React.useState(false);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [showSSOSelectBox, setShowSSOSelectBox] = React.useState(false);

  const magicClient = createMagic();

  const { data, loading, error } = useAuth();
  if (data && data.user && !isTransitioning) {
    setIsTransitioning(true);
    router.push(`/org/${data.org._id}`);
    return <Loading />;
  }

  const handleLogIn = async (event) => {
    event.preventDefault();
    setShowLoading(true);
    const email = event.target.elements.email.value;
    if (!email || email === '') {
      toaster.error('Please enter an email');
      setShowLoading(false);
      return;
    }

    if (SSO_ORG_DOMAINS.includes(email.split('@')[1])) {
      toaster.error('Please use SSO to login with this org');
      setShowLoading(false);
      return;
    }

    const checkOrgExistsRes = await sendMessage(CHECK_ORG_EXISTS_MESSAGE, {
      email,
    });
    if (!checkOrgExistsRes.ok) {
      toaster.error(checkOrgExistsRes.reason || 'Something went wrong');
      setShowLoading(false);
      return;
    }

    if (!checkOrgExistsRes.exists) {
      toaster.error('No organization found for this email');
      setShowLoading(false);
      return;
    }

    const did = await magicClient.auth.loginWithMagicLink({ email: email });
    const loginRes = await sendMessage(LOGIN_MESSAGE, { email, did });

    if (!loginRes.ok) {
      toaster.error(loginRes.reason || 'Something went wrong');
      setShowLoading(false);
    } else {
      if (!isTransitioning) {
        setIsTransitioning(true);
        router.push(`/org/${loginRes.data.orgID}`);
      }
    }
  };

  const handleOktaLogin = async (selectedOktaOrg) => {
    if (!selectedOktaOrg) {
      toaster.error('Please select an organization');
      return;
    }
    setLoadingMessage('Connecting to Okta Server...');
    setShowLoading(true);

    const OktaClient = createOktaClient(selectedOktaOrg.orgID);
    OktaClient.setOriginalUri();
    await OktaClient.token.getWithRedirect({
      state: `${selectedOktaOrg.orgID}:/org/${selectedOktaOrg.orgID}`, // will be URI encoded
      redirect_uri: OKTA_CALLBACK_URL,
      responseMode: 'query',
    });
  };

  if (loading || showLoading || isTransitioning)
    return <Loading msg={loadingMessage} />;

  return (
    <>
      <Head>
        <title>Sign In - StartScreen</title>
        <meta property="og:title" content="StartScreen" />
        <meta
          property="og:description"
          content="The next-generation employee training platform."
        />
      </Head>

      <div class="aliased bg-gray-50 min-h-[100vh] relative overflow-hidden">
        <section class="max-w-6xl mx-auto px-5 md:px-6">
          <div class="py-10" data-aos="fade-up">
            <div class="min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8">
              <div class="sm:mx-auto sm:w-full sm:max-w-md">
                <Link href="/">
                  <img
                    class="mx-auto h-12 w-auto cursor-pointer"
                    src="/short-logo.svg"
                  />
                </Link>
                <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900 tracking-tight">
                  Sign into your account
                </h2>
                <p class="mt-2 text-center text-sm text-gray-700">
                  Or{' '}
                  <Link href="/signup">
                    <a className="font-medium text-indigo-600 hover:text-gray-900 inline-flex transition-all duration-200 ease-in-out">
                      create a free account
                    </a>
                  </Link>
                </p>
              </div>
              <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div class="bg-white py-8 px-4 shadow rounded-md sm:px-10">
                  <form class="space-y-5" onSubmit={handleLogIn}>
                    <div>
                      <div class="mt-1">
                        <div className="relative">
                          <input
                            type="email"
                            name="email"
                            placeholder="Enter your work email"
                            className="pl-11 p-2 shadow-sm border-0 ring-1 ring-gray-300 rounded-md text-gray-500 text-sm w-full outline-none focus:outline-none focus:ring-2 focus:ring-indigo-200 placeholder:text-gray-400 leading-5 transition-all duration-200 ease-in-out"
                          ></input>
                          <EnvelopeIcon className="pointer-events-none absolute top-2 left-3 h-5 w-5 stroke-gray-400" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <button
                        type="submit"
                        class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 active:scale-95 focus:outline-none transition-all duration-200 ease-in-out"
                      >
                        {' '}
                        Sign in with email{' '}
                      </button>
                    </div>
                    <hr className="w-full border-t-gray-200 border-solid border" />

                    <div>
                      {showSSOSelectBox ? (
                        <SsoLoginSelector handleOktaLogin={handleOktaLogin} />
                      ) : (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setShowSSOSelectBox(true);
                          }}
                          class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md border-gray-300 shadow-sm text-sm text-gray-500 bg-white hover:bg-gray-200 active:scale-95 focus:outline-none transition-all duration-200 ease-in-out"
                        >
                          Sign in with SSO
                        </button>
                      )}
                    </div>
                    <p class="text-center text-xs text-gray-500">
                      By signing in, you agree to our{' '}
                      <a
                        class="text-indigo-600 hover:text-gray-900 transition-all duration-200 ease-in-out"
                        href="/terms"
                        target="_blank"
                      >
                        Terms of Use
                      </a>{' '}
                      and{' '}
                      <a
                        class="text-indigo-600 hover:text-gray-900 transition-all duration-200 ease-in-out"
                        href="/privacy"
                        target="_blank"
                      >
                        Privacy Policy
                      </a>
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SignInPage;
